import { PremiumTiers } from '@bridebook/models/source/models/Suppliers/Admin/admin.types';
import { getSearchUrlWithCountrySuffix } from '@bridebook/toolbox/src/getSearchUrl';
import {
  getIsVenue,
  getPremium,
  getSupplier,
  getSupplierCountry,
  getSupplierCounty,
} from '@bridebook/web/app-basic/lib/supplier/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { getAreaContext } from '@bridebook/web/app-shared/lib/supplier/utils/get-location-context';
import { getDefaultSearchArea } from '@bridebook/web/lib/search/utils';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

interface Options {
  /**
   * Send limited profile signups to a venue search once onboarding finishes,
   * instead of leaving them on the home page.
   */
  searchAfterOnboarding?: boolean;
  /**
   * Query params appended when the user skips onboarding and returns to the profile.
   * Ignored for limited profiles that route through onboarding or venue search.
   */
  profilePageQuery?: Record<string, string>;
}

const buildProfileNextPath = (supplierPath: string, profilePageQuery?: Record<string, string>) => {
  if (!profilePageQuery || Object.keys(profilePageQuery).length === 0) {
    return supplierPath;
  }

  const queryString = new URLSearchParams(profilePageQuery).toString();
  return encodeURIComponent(`${supplierPath}?${queryString}`);
};

/**
 * Builds the signup query for the logged-out supplier profile CTAs, deciding where a
 * new user lands once they finish signing up.
 *
 * Paying profiles skip onboarding and go straight back to the profile, optionally with
 * extra query params. Limited profiles (Unverified tier) keep onboarding, optionally
 * with a venue search as the destination afterwards. Keeping this in one place stops
 * the profile CTAs from drifting apart.
 */
export const useSignupRedirectProps = ({
  searchAfterOnboarding = false,
  profilePageQuery,
}: Options = {}) => {
  const router = useRouter();
  const isVenue = useSelector(getIsVenue);
  const premium = useSelector(getPremium);
  const supplier = useSelector(getSupplier);
  const l10nCountryCode = useSelector(getSupplierCountry);
  const supplierCounty = useSelector(getSupplierCounty);

  const address = supplier?.address;

  return useMemo(() => {
    const isLimitedProfile = premium?.tier === PremiumTiers.Unverified;

    if (isLimitedProfile && isVenue) {
      // Leaving `skipOnboarding` out is what routes through onboarding, because that
      // flag is what the signup modal branches on.
      if (!searchAfterOnboarding) {
        return { query: '', queryOnlyForNonSuppliers: false };
      }

      // Logged out pages have no search area of their own, so fall back to the county
      // of the profile the user came from, then to the supplier's country.
      const countryCode = address?.country || l10nCountryCode;
      const searchPath = getSearchUrlWithCountrySuffix({
        slug: 'venue',
        area: supplierCounty || getDefaultSearchArea({ countryCode }),
        areaContext: getAreaContext(address || null, countryCode),
        as: true,
        countryCode,
      }).replace(/^\//, '');

      return { query: `?next=${searchPath}`, queryOnlyForNonSuppliers: false };
    }

    // Current path without the market prefix and without any existing query string.
    const supplierPath = router.asPath.split('?')[0].split('/').slice(2).join('/');
    const nextPath = buildProfileNextPath(supplierPath, profilePageQuery);

    return {
      query: `?skipOnboarding=true&next=${nextPath}`,
      queryOnlyForNonSuppliers: false,
    };
  }, [
    premium?.tier,
    isVenue,
    searchAfterOnboarding,
    profilePageQuery,
    address,
    l10nCountryCode,
    supplierCounty,
    router.asPath,
  ]);
};
